/**
 * What a guard is already working, and whether one more shift would fit.
 *
 * The ledger holds every shift a guard has at any site plus whatever the
 * draft has given them so far, at every site in the run, because rest, runs of
 * working days and the averages are judged across the company and across the
 * draft as it grows.
 *
 * `findBlocker` forecasts the roster rules so the planner does not propose
 * what the dry run would only throw back. It is deliberately at least as
 * strict as the evaluators: a shift may not even touch another one, where the
 * database would merge the two into a longer shift and judge that instead.
 */

import { windowMinutes } from '../rosterCompliance/rosterModel';
import { PLANNING_POLICY, nightMinutesOf } from './shiftPatterns';
import {
  dateOfMinute,
  lastDateOfShift,
  toDayNumber,
  weekStartDayNumber,
  zonedMinuteToUtcMs,
} from './time';
import type {
  BlockerCode,
  DemandBlock,
  NightWorkRule,
  ScheduleGuard,
  ScheduleRule,
  ScheduleSite,
  TimeWindow,
  WorkedShift,
} from './types';

const MS_PER_MINUTE = 60_000;

export interface LedgerEntry {
  /** The demand block this came from; null for work rostered before the draft. */
  blockKey: string | null;
  /** The roster date as a day number: runs and averages count by roster date. */
  day: number;
  startMs: number;
  endMs: number;
  dutyMinutes: number;
  /** Minutes from midnight of the roster date, for counting night work under any site's rule. */
  dutySegments: TimeWindow[];
}

/** Night minutes in a ledger entry, counted against the night period of the rule judging it. */
export const entryNightMinutes = (entry: LedgerEntry, night: NightWorkRule | null): number => (
  entry.dutySegments.reduce((total, segment) => total + nightMinutesOf(segment, night), 0)
);

/** A demand block with the instants and totals the rule checks need. */
export interface TimedBlock {
  block: DemandBlock;
  /** The rule of the block's site, which judges it. */
  rule: ScheduleRule | null;
  day: number;
  firstDate: string;
  lastDate: string;
  startMs: number;
  endMs: number;
  dutyMinutes: number;
  nightMinutes: number;
}

export function timeBlock(block: DemandBlock, site: ScheduleSite, rule: ScheduleRule | null): TimedBlock {
  return {
    block,
    day: toDayNumber(block.slotDate),
    firstDate: dateOfMinute(block.slotDate, block.startMinute),
    lastDate: lastDateOfShift(block.slotDate, block.endMinute),
    startMs: zonedMinuteToUtcMs(block.slotDate, block.startMinute, site.timezone),
    endMs: zonedMinuteToUtcMs(block.slotDate, block.endMinute, site.timezone),
    dutyMinutes: windowMinutes(block),
    nightMinutes: nightMinutesOf(block, rule?.night ?? null),
    rule,
  };
}

const fromWorkedShift = (shift: WorkedShift): LedgerEntry => ({
  blockKey: null,
  day: toDayNumber(shift.slotDate),
  startMs: shift.startMs,
  endMs: shift.endMs,
  dutyMinutes: shift.dutyMinutes,
  dutySegments: shift.dutySegments,
});

export class GuardLedger {
  private entries: LedgerEntry[];

  constructor(shifts: WorkedShift[] = []) {
    this.entries = shifts.map(fromWorkedShift);
  }

  all(): readonly LedgerEntry[] {
    return this.entries;
  }

  add(timed: TimedBlock): void {
    this.entries.push({
      blockKey: timed.block.key,
      day: timed.day,
      startMs: timed.startMs,
      endMs: timed.endMs,
      dutyMinutes: timed.dutyMinutes,
      dutySegments: [{ startMinute: timed.block.startMinute, endMinute: timed.block.endMinute }],
    });
  }

  remove(blockKey: string): void {
    this.entries = this.entries.filter((entry) => entry.blockKey !== blockKey);
  }

  /** Duty minutes on roster dates from `fromDay` to `toDay`, both included. */
  minutesBetween(fromDay: number, toDay: number): number {
    return this.entries.reduce(
      (total, entry) => (entry.day >= fromDay && entry.day <= toDay ? total + entry.dutyMinutes : total),
      0,
    );
  }

  /** Duty minutes in the Monday-to-Sunday week a day falls in. */
  weekMinutes(day: number): number {
    const monday = weekStartDayNumber(day);
    return this.minutesBetween(monday, monday + 6);
  }

  draftedMinutesBetween(fromDay: number, toDay: number): number {
    return this.entries.reduce(
      (total, entry) => (
        entry.blockKey !== null && entry.day >= fromDay && entry.day <= toDay ? total + entry.dutyMinutes : total
      ),
      0,
    );
  }

  worksOn(day: number): boolean {
    return this.entries.some((entry) => entry.day === day);
  }
}

/** Working days in a row if the guard also worked `day`. */
function runLengthThrough(ledger: GuardLedger, day: number): number {
  let length = 1;
  for (let before = day - 1; ledger.worksOn(before); before -= 1) length += 1;
  for (let after = day + 1; ledger.worksOn(after); after += 1) length += 1;
  return length;
}

/**
 * The roster dates whose trailing window the new shift lands in: its own, and
 * every later date the guard already works inside one window's reach. Those
 * are the dates the evaluators will judge an average on.
 */
function averageCheckDays(ledger: GuardLedger, timed: TimedBlock, windowDays: number): number[] {
  const days = new Set<number>([timed.day]);
  ledger.all().forEach((entry) => {
    if (entry.day > timed.day && entry.day <= timed.day + windowDays - 1) days.add(entry.day);
  });
  return Array.from(days);
}

function breaksWeeklyAverage(ledger: GuardLedger, timed: TimedBlock, rule: ScheduleRule | null): boolean {
  if (!rule?.maxAverageWeeklyMinutes || rule.maxAverageWeeklyMinutes <= 0) return false;
  const weeks = Math.max(rule.weeklyReferenceWeeks ?? 1, 1);
  const windowDays = weeks * 7;
  const limit = rule.maxAverageWeeklyMinutes * weeks;

  return averageCheckDays(ledger, timed, windowDays).some((day) => (
    ledger.minutesBetween(day - (windowDays - 1), day) + timed.dutyMinutes > limit
  ));
}

function breaksNightAverage(ledger: GuardLedger, timed: TimedBlock, rule: ScheduleRule | null): boolean {
  const night = rule?.night ?? null;
  if (!night || (night.maxAverageWeeklyMinutes === null && night.maxAverage24hMinutes === null)) return false;
  const windowDays = Math.max(night.referenceDays, 1);

  return averageCheckDays(ledger, timed, windowDays).some((day) => {
    const inWindow = ledger.all().filter((entry) => entry.day >= day - (windowDays - 1) && entry.day <= day);
    // The averages only bind somebody who works nights in the reference period.
    const worksNights = timed.nightMinutes >= night.minimumNightMinutes
      || inWindow.some((entry) => entryNightMinutes(entry, night) >= night.minimumNightMinutes);
    if (!worksNights) return false;

    const total = inWindow.reduce((sum, entry) => sum + entry.dutyMinutes, timed.dutyMinutes);
    return (night.maxAverageWeeklyMinutes !== null && total > (night.maxAverageWeeklyMinutes / 7) * windowDays)
      || (night.maxAverage24hMinutes !== null && total > night.maxAverage24hMinutes * windowDays);
  });
}

/**
 * The first reason this guard cannot take this shift, or null if they can.
 * Ordered from the plainest fact to the subtlest, so the reason reported for
 * an unfilled shift is the one an admin would act on first.
 */
export function findBlocker(
  guard: ScheduleGuard,
  ledger: GuardLedger,
  timed: TimedBlock,
  rule: ScheduleRule | null,
): BlockerCode | null {
  if (guard.restrictedSiteIds && !guard.restrictedSiteIds.includes(timed.block.siteId)) {
    return 'other_site';
  }

  const licenceTypeId = timed.block.requiredLicenceTypeId;
  if (licenceTypeId && !guard.licences.some((licence) => (
    licence.licenceTypeId === licenceTypeId && licence.expiryDate >= timed.lastDate
  ))) {
    return 'no_licence';
  }

  if (guard.leave.some((leave) => leave.startDate <= timed.lastDate && leave.endDate >= timed.firstDate)) {
    return 'on_leave';
  }

  if (guard.availability.some((window) => (
    window.type === 'unavailable' && window.startMs < timed.endMs && window.endMs > timed.startMs
  ))) {
    return 'unavailable';
  }

  const minimumRestMs = (rule?.minimumRestMinutes ?? PLANNING_POLICY.minimumRestMinutes) * MS_PER_MINUTE;
  let shortRest = false;
  for (const entry of ledger.all()) {
    if (entry.startMs < timed.endMs && entry.endMs > timed.startMs) return 'overlap';
    const gap = entry.endMs <= timed.startMs ? timed.startMs - entry.endMs : entry.startMs - timed.endMs;
    if (gap < minimumRestMs) shortRest = true;
  }
  if (shortRest) return 'short_rest';

  const maxDays = Math.floor(rule?.maxConsecutiveDays ?? 0) || PLANNING_POLICY.maxConsecutiveDays;
  if (runLengthThrough(ledger, timed.day) > maxDays) return 'consecutive_days';

  if (breaksWeeklyAverage(ledger, timed, rule)) return 'weekly_average';
  if (breaksNightAverage(ledger, timed, rule)) return 'night_limit';

  return null;
}

/** Whether a declared window makes this shift one the guard asked for, is open to, or neither. */
export function availabilityPreference(guard: ScheduleGuard, timed: TimedBlock): 'preferred' | 'available' | null {
  const overlapping = guard.availability.filter((window) => window.startMs < timed.endMs && window.endMs > timed.startMs);
  if (overlapping.some((window) => window.type === 'preferred')) return 'preferred';
  if (overlapping.some((window) => window.type === 'available')) return 'available';
  return null;
}
